/**
 * Transfert de session cross-front sans JWT dans l’URL (évite Safe Browsing Chrome).
 * Prod (*.agrosilo-solutions.com) : cookie Domain partagé, TTL courte, lecture unique.
 * Localhost / hors domaine : pas de cookie partagé → repli query `auth=` (comportement historique).
 */

const COOKIE_PREFIX = 'erp_ah';
const COOKIE_COUNT = `${COOKIE_PREFIX}_n`;
const CHUNK_SIZE = 2800;
const MAX_AGE_SEC = 120;

export function getAuthHandoffCookieDomain(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const host = String(window.location.hostname || '')
    .trim()
    .toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
    return null;
  }
  if (host === 'agrosilo-solutions.com' || host.endsWith('.agrosilo-solutions.com')) {
    return '.agrosilo-solutions.com';
  }
  return null;
}

function cookieBaseAttrs(domain: string | null): string {
  const parts = [`Path=/`, `Max-Age=${MAX_AGE_SEC}`, `SameSite=Lax`];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  return parts.join('; ');
}

function setCookie(name: string, value: string, domain: string | null): void {
  document.cookie = `${name}=${value}; ${cookieBaseAttrs(domain)}`;
}

function deleteCookie(name: string, domain: string | null): void {
  const parts = [`Path=/`, `Max-Age=0`, `SameSite=Lax`];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  document.cookie = `${name}=; ${parts.join('; ')}`;
  // Sans Domain aussi (cookie host-only éventuel)
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const parts = String(document.cookie || '').split(';');
  for (const raw of parts) {
    const part = raw.trim();
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }
  return null;
}

function clearHandoffCookies(domain: string | null): void {
  const nRaw = readCookie(COOKIE_COUNT);
  const n = nRaw ? Number(nRaw) : 0;
  deleteCookie(COOKIE_COUNT, domain);
  const limit = Number.isFinite(n) && n > 0 ? Math.min(n, 40) : 40;
  for (let i = 0; i < limit; i++) {
    deleteCookie(`${COOKIE_PREFIX}_${i}`, domain);
  }
}

/**
 * Stocke le payload session en cookies fragmentés.
 * @returns true si le handoff cookie est actif (ne pas mettre `auth=` dans l’URL)
 */
export function storeAuthHandoffPayload(payload: Record<string, unknown>): boolean {
  const domain = getAuthHandoffCookieDomain();
  if (!domain) {
    return false;
  }
  let encoded: string;
  try {
    encoded = btoa(JSON.stringify(payload));
  } catch {
    return false;
  }
  if (!encoded) {
    return false;
  }

  clearHandoffCookies(domain);

  const chunks: string[] = [];
  for (let i = 0; i < encoded.length; i += CHUNK_SIZE) {
    chunks.push(encoded.slice(i, i + CHUNK_SIZE));
  }
  if (chunks.length === 0 || chunks.length > 40) {
    return false;
  }

  try {
    setCookie(COOKIE_COUNT, String(chunks.length), domain);
    chunks.forEach((chunk, i) => {
      setCookie(`${COOKIE_PREFIX}_${i}`, encodeURIComponent(chunk), domain);
    });
    // Vérifie que le navigateur a bien accepté au moins le compteur
    if (readCookie(COOKIE_COUNT) !== String(chunks.length)) {
      clearHandoffCookies(domain);
      return false;
    }
    return true;
  } catch {
    clearHandoffCookies(domain);
    return false;
  }
}

/** Lit + efface le handoff cookie. Retourne le payload ou null. */
export function consumeAuthHandoffPayload(): Record<string, unknown> | null {
  const domain = getAuthHandoffCookieDomain();
  // Même hors domaine partagé, tenter une lecture host-only (dev / edge cases)
  const nRaw = readCookie(COOKIE_COUNT);
  if (!nRaw) {
    return null;
  }
  const n = Number(nRaw);
  if (!Number.isFinite(n) || n <= 0 || n > 40) {
    clearHandoffCookies(domain);
    return null;
  }

  let encoded = '';
  try {
    for (let i = 0; i < n; i++) {
      const part = readCookie(`${COOKIE_PREFIX}_${i}`);
      if (part == null) {
        clearHandoffCookies(domain);
        return null;
      }
      encoded += decodeURIComponent(part);
    }
    clearHandoffCookies(domain);
    return JSON.parse(atob(encoded)) as Record<string, unknown>;
  } catch {
    clearHandoffCookies(domain);
    return null;
  }
}
